<template>
    <div v-if="hasSections" class="navbar__submenu" :class="{'navbar__submenu--open': isOpen}">
        <div class="navbar__submenu__left">
            <div
                v-for="(section, index) in sectionsWithItemsOrLink"
                :key="`group-${section.label}`"
                class="navbar__submenu-group"
                :class="{'navbar__submenu-group--open': !!isOpenSection[index]}"
                :data-testid="`navbar-submenu-item-${parentIndex}-${index}`"
            >
                <NuxtLink
                    v-if="section.href && !(section.items && section.items.length > 0)"
                    :to="$withLocalePrefix(section.href)"
                    class="navbar__submenu-title"
                    exact-active-class="navbar__submenu-item--active"
                    @click="$emit('close-menu')"
                >
                    {{ section.label }}
                </NuxtLink>
                <template v-else>
                    <button
                        :id="`navbar-submenu-section-${index}`"
                        type="button"
                        class="navbar__submenu-header"
                        :aria-expanded="!!isOpenSection[index]"
                        :aria-controls="`navbar-submenu-section-${index}-content`"
                        data-testid="navbar-submenu-section-header"
                        @click="toggleSection(index)"
                    >
                        <h3 class="navbar__submenu-title">{{ section.label }}</h3>
                        <span class="navbar__submenu-chevron">
                            <VJoyIcon name="chevron-down" class="navbar__submenu-chevron" color="neutral-50" />
                        </span>
                    </button>
                    <div
                        :id="`navbar-submenu-section-${index}-content`"
                        class="navbar__submenu-panel"
                        :aria-labelledby="`navbar-submenu-section-${index}`"
                        data-testid="navbar-submenu-section-panel"
                    >
                        <ul :class="['navbar__submenu-items', {'navbar__submenu-items--open': !!isOpenSection[index]}]">
                            <li v-for="(item, j) in section.items" :key="`item-${j}`">
                                <NuxtLink
                                    :to="$withLocalePrefix(item.href!)"
                                    exact-active-class="navbar__submenu-item--active"
                                    @click="$emit('close-menu')"
                                >
                                    {{ item.label }}
                                </NuxtLink>
                            </li>
                        </ul>
                    </div>
                </template>
            </div>
        </div>
        <div v-if="featureCards?.length" class="navbar__submenu__right" :data-testid="`navbar-submenu-feature-card-group-${parentIndex}`">
            <NavbarFeatureCard
                v-for="(featureCard, index) in featureCards"
                :key="`feature-card--${parentIndex}-${index}`"
                :data-testid="`navbar-submenu-feature-card-${parentIndex}-${index + 1}`"
                :section="featureCard"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import type {NavbarItem, NavbarSection} from '@navbar-unlogged-types';
    import {NuxtLink} from '#components';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import NavbarFeatureCard from './NavbarFeatureCard.vue';

    defineEmits<{
        'close-menu': [];
    }>();
    const props = defineProps<{
        isOpen: boolean;
        parentIndex: number;
        sections: NavbarItem[];

        featureCards?: NavbarSection[];
    }>();

    const hasSections = computed(() => props.sections && props.sections.length > 0);
    const sectionsWithItemsOrLink = computed(() => props.sections.filter((section) => (section.items && section.items.length > 0) || !!section.href));

    const isOpenSection = ref<Record<number, boolean>>({0: true});

    function toggleSection(index: number) {
        isOpenSection.value[index] = !isOpenSection.value[index];
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .navbar__submenu {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 2;
        height: 100%;
        display: flex;
        flex-direction: column;
        background-color: var(--joy-color-neutral-0);
        padding: var(--joy-core-spacing-8) var(--joy-core-spacing-5);
        overflow-y: auto;
        /* anim */
        transform: translate3d(100%, 0, 0);
        transition: transform var(--navbar-transition-duration) var(--navbar-transition-easing);
        will-change: transform;
        backface-visibility: hidden;

        &--open {
            transform: translate3d(0, 0, 0);
            border-top: 1px solid var(--joy-color-neutral-30);
        }

        &__left {
            display: block;
        }

        &__right {
            display: flex;
            flex-direction: column;
            margin-top: auto;
            padding-top: var(--joy-core-spacing-8);
            gap: var(--joy-core-spacing-5);
        }

        &-header {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: var(--joy-core-spacing-3);
            width: 100%;
        }

        &-title {
            @include text.getFontBySize(primary-650);
            color: var(--joy-color-neutral-70);
            letter-spacing: -0.2px;
        }

        &-chevron {
            display: inline-flex;
            transition: transform var(--joy-transition-duration-default) var(--joy-transition-timing-function);
        }

        &-group {
            margin-bottom: var(--joy-core-spacing-8);

            &:last-child {
                margin-bottom: 0;
            }

            &--open {
                .navbar__submenu-chevron {
                    transform: rotate(180deg);
                }

                .navbar__submenu-panel {
                    grid-template-rows: 1fr;
                }
            }
        }

        &-panel {
            display: grid;
            grid-template-rows: 0fr;
            transition: grid-template-rows var(--joy-transition-duration-long) var(--joy-transition-timing-function);
        }

        &-items {
            position: relative;
            overflow: hidden;
            list-style: none;
            transition: all var(--joy-transition-duration-long) var(--joy-transition-timing-function);

            &::before {
                content: '';
                position: absolute;
                border-left: 1px solid var(--joy-color-neutral-20);
                left: 0.9px;
                height: 100%;
                width: 100%;
            }

            &--open {
                padding: var(--joy-core-spacing-1) 0;
                margin-top: var(--joy-core-spacing-4);
            }

            li {
                padding-left: var(--joy-core-spacing-4);
                &:not(:last-child) {
                    padding-bottom: var(--joy-core-spacing-4);
                }

                a {
                    position: relative;
                    display: block;
                    color: var(--joy-color-neutral-60);
                    text-decoration: none;
                    font-size: var(--joy-font-size-primary-400);
                    font-weight: 400;
                    line-height: 1.5;

                    &::before {
                        content: '';
                        position: absolute;
                        top: 13px;
                        left: -16px;
                        width: 4px;
                        height: 30px;
                        transform: translateY(-50%);
                        background-color: var(--joy-color-neutral-70);
                        border-radius: var(--joy-core-radius-4);
                        opacity: 0;
                        transition: opacity var(--joy-transition-duration-short) var(--joy-transition-timing-function);
                    }

                    &:hover::before,
                    &:focus-visible::before,
                    &.navbar__submenu-item--active::before {
                        opacity: 1;
                    }

                    &:first-child {
                        padding-top: 0;
                    }
                }
            }
        }
    }

    /* desktop */
    @include mq.screen_sm(min) {
        .navbar__submenu {
            position: absolute;
            top: calc(100% + 1px);
            left: calc(-1 * var(--joy-core-spacing-7));
            right: calc(-1 * var(--joy-core-spacing-7));
            padding: var(--joy-core-spacing-6);
            margin: 0;
            max-height: calc(100vh - var(--topnav-height) - var(--navbar-height));
            height: auto;
            overflow-y: auto;
            background-color: var(--joy-color-neutral-0);
            transform: translate3d(0, 0, 0);
            box-shadow: 0 6px 12px -2px rgba(0, 0, 0, 0.08);
            transition: none;
            display: none;
            flex-direction: row;
            flex-wrap: wrap;
            gap: var(--joy-core-spacing-10);
            align-items: flex-start;

            &__left {
                display: flex;
                gap: var(--joy-core-spacing-8);
            }

            &__right {
                display: flex;
                flex-wrap: wrap;
                flex-direction: row;
                gap: 44px;
                margin-left: auto;
                justify-content: center;
                margin-top: 0;
                padding-top: 0;
            }

            &-header {
                pointer-events: none;
                cursor: default;
            }

            &-chevron {
                display: none;
            }

            &-title {
                @include text.getFontBySize(primary-450);
                color: var(--joy-color-neutral-70);
                letter-spacing: -0.2px;
            }

            &-group {
                display: flex;
                flex-direction: column;
                max-width: 100%;
                margin-bottom: 0;
            }

            &-panel {
                transition: none;
                display: flex;
                flex-direction: column;
                flex: 1;
            }

            &-items {
                padding: 0;
                list-style-type: none;
                user-select: none;
                margin-top: var(--joy-core-spacing-4);
                transition: none;
                overflow: visible;
                flex: 1;

                li {
                    a {
                        white-space: nowrap;
                    }
                }
            }
        }
    }
</style>
